import { Component, forwardRef, Input, OnDestroy, OnInit } from '@angular/core';
import {
  ControlValueAccessor,
  FormBuilder,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';

import { Subscription, tap } from 'rxjs';

import { CommonRadioButtonItem } from '@tablesready/common-ui';

@Component({
  selector: 'tablesready-size',
  templateUrl: './size.component.html',
  styleUrls: ['./size.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SizeComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: SizeComponent,
      multi: true,
    },
  ],
})
export class SizeComponent implements OnInit, OnDestroy, ControlValueAccessor, Validator {
  @Input() defaultSize: number;
  form: FormGroup;
  sizesRadioItems: CommonRadioButtonItem[] = [1, 2, 3, 4, 5, 6, 7].map((size) => ({
    name: String(size),
    value: size,
  }));
  private onChangeMethod: (value: number | string | boolean) => unknown;
  private onTouchedMethod: () => unknown;
  private readonly subscriptions = new Subscription();

  constructor(
    private readonly formBuilder: FormBuilder,
  ) {
  }

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      size: [this.defaultSize, []],
      sizeOther: this.formBuilder.control(null, {
        validators: [Validators.min(1), Validators.pattern(/\d*/)],
        updateOn: 'change',
      }),
    });

    const sizeSubscription= this.form.get('size').valueChanges
      .pipe(tap(size => {
        this.form.get('sizeOther').setValue(size, {emitEvent: false});
        if (this.onChange) {
          this.onChange(size);
        }
      }))
      .subscribe();
    this.subscriptions.add(sizeSubscription);

    const sizeOtherSubscription= this.form.get('sizeOther').valueChanges
      .pipe(
        tap(size => {
          if (size < 1) {
            size = 1;
          }
          this.form.get('size').setValue(size, {emitEvent: false});
          this.form.get('sizeOther').setValue(size, {emitEvent: false});

          if (this.onChange) {
            this.onChange(size);
          }
        }),
      )
      .subscribe();
    this.subscriptions.add(sizeOtherSubscription);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  registerOnChange(onChangeMethod: () => unknown): void {
    this.onChangeMethod = onChangeMethod;
  }

  registerOnTouched(onTouchedMethod: () => unknown): void {
    this.onTouchedMethod = onTouchedMethod;
  }

  writeValue(size: number | string | null): void {
    size = size || this.defaultSize;
    if (size < 8) {
      this.form.setValue({size, sizeOther: null}, {emitEvent: false});
      return;
    }

    this.form.setValue({sizeOther: size, size},{emitEvent: false});
  }

  onChange(newValue: number | string | boolean): void {
    this.onChangeMethod(newValue);
  }

  setDisabledState(disabled: boolean): void {
    disabled ? this.form.disable() : this.form.enable();
  }

  validate(): ValidationErrors | null {
    return this.form.get('sizeOther').errors;
  }
}
